import { NotificationTracker } from '../utils/NotificationTracker';
const STORAGE_KEY = 'web_notification_wrapper_pending';
const STORAGE_KEY_SCHEDULED = 'web_notification_wrapper_scheduled';
export class WebNotificationWrapper {
    platform = 'web';
    config;
    scheduledNotifications = new Map();
    pendingNotifications = new Map();
    isInitialized = false;
    constructor(config = {}) {
        this.config = {
            maxRetries: 3,
            retryDelay: 1000,
            ...config
        };
    }
    async initialize() {
        if (this.isInitialized)
            return true;
        try {
            // Check if notifications are supported
            if (!this.isSupported()) {
                console.warn('[WebNotificationWrapper] Notifications not supported in this browser');
                return false;
            }
            // Register service worker if specified
            if (this.config.serviceWorkerPath && 'serviceWorker' in navigator) {
                try {
                    await navigator.serviceWorker.register(this.config.serviceWorkerPath);
                    console.log('[WebNotificationWrapper] Service worker registered successfully');
                }
                catch (error) {
                    console.warn('[WebNotificationWrapper] Service worker registration failed:', error);
                }
            }
            // Restore pending notifications from localStorage
            await this.restorePendingNotifications();
            // Set up event listeners
            this.setupEventListeners();
            this.isInitialized = true;
            console.log('[WebNotificationWrapper] Initialized successfully');
            return true;
        }
        catch (error) {
            console.error('[WebNotificationWrapper] Initialization failed:', error);
            return false;
        }
    }
    async destroy() {
        // Clear all scheduled notifications
        this.scheduledNotifications.forEach(timeoutId => {
            clearTimeout(timeoutId);
        });
        this.scheduledNotifications.clear();
        this.pendingNotifications.clear();
        // Clear localStorage
        this.clearStorage();
        this.isInitialized = false;
        console.log('[WebNotificationWrapper] Destroyed');
    }
    isSupported() {
        return 'Notification' in window;
    }
    getPermissionStatus() {
        if (!this.isSupported())
            return 'denied';
        return Notification.permission;
    }
    async requestPermission() {
        if (!this.isSupported()) {
            return { granted: false, permission: 'denied' };
        }
        try {
            const permission = await Notification.requestPermission();
            const granted = permission === 'granted';
            console.log(`[WebNotificationWrapper] Permission ${granted ? 'granted' : 'denied'}`);
            return { granted, permission };
        }
        catch (error) {
            console.error('[WebNotificationWrapper] Permission request failed:', error);
            return { granted: false, permission: 'denied' };
        }
    }
    async sendImmediate(payload) {
        if (!this.isSupported()) {
            throw new Error('Notifications not supported');
        }
        if (this.getPermissionStatus() !== 'granted') {
            throw new Error('Notification permission not granted');
        }
        try {
            // Check if we're on a mobile browser that requires service worker notifications
            if (this.isMobileBrowser() && 'serviceWorker' in navigator) {
                await this.sendServiceWorkerNotification(payload);
            }
            else {
                // Use standard Notification constructor for desktop browsers
                const notification = new Notification(payload.title, {
                    body: payload.body,
                    icon: payload.icon,
                    badge: payload.badge,
                    tag: payload.tag,
                    data: payload.data,
                    requireInteraction: payload.requireInteraction,
                    silent: payload.silent
                });
                // Set up click handler
                notification.onclick = () => {
                    this.handleNotificationClick(payload);
                    notification.close();
                };
                // Auto-close after 5 seconds if not requiring interaction
                if (!payload.requireInteraction) {
                    setTimeout(() => {
                        notification.close();
                    }, 5000);
                }
            }
            console.log('[WebNotificationWrapper] Immediate notification sent:', payload.title);
        }
        catch (error) {
            console.error('[WebNotificationWrapper] Failed to send immediate notification:', error);
            throw error;
        }
    }
    async schedule(payload) {
        const notificationId = payload.id || `web-notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const delay = payload.scheduleTime.getTime() - Date.now();
        if (delay <= 0) {
            // If scheduled time is in the past, send immediately
            await this.sendImmediate(payload);
            return notificationId;
        }
        // Track the scheduled notification
        NotificationTracker.trackScheduled({
            id: notificationId,
            title: payload.title,
            body: payload.body,
            challengeId: payload.data?.challengeId,
            challengeTitle: payload.data?.challengeTitle,
            scheduledFor: payload.scheduleTime.toISOString(),
            platform: 'web',
            data: payload.data
        });
        // Store the pending notification
        this.pendingNotifications.set(notificationId, payload);
        this.savePendingNotifications();
        // Schedule the notification
        const timeoutId = window.setTimeout(async () => {
            try {
                await this.sendImmediate(payload);
                this.pendingNotifications.delete(notificationId);
                this.scheduledNotifications.delete(notificationId);
                this.savePendingNotifications();
                // Track the sent notification
                NotificationTracker.trackSent(notificationId);
                console.log(`[WebNotificationWrapper] Scheduled notification sent: ${payload.title}`);
            }
            catch (notificationError) {
                console.error('[WebNotificationWrapper] Failed to send scheduled notification:', notificationError);
                this.pendingNotifications.delete(notificationId);
                this.scheduledNotifications.delete(notificationId);
                this.savePendingNotifications();
            }
        }, delay);
        this.scheduledNotifications.set(notificationId, timeoutId);
        this.saveScheduledNotifications();
        console.log(`[WebNotificationWrapper] Notification scheduled for ${payload.scheduleTime.toISOString()}: ${payload.title}`);
        return notificationId;
    }
    async cancel(notificationId) {
        const timeoutId = this.scheduledNotifications.get(notificationId);
        if (timeoutId) {
            clearTimeout(timeoutId);
            this.scheduledNotifications.delete(notificationId);
            this.pendingNotifications.delete(notificationId);
            this.savePendingNotifications();
            this.saveScheduledNotifications();
            // Track the cancelled notification
            NotificationTracker.trackCancelled(notificationId);
            console.log(`[WebNotificationWrapper] Cancelled notification: ${notificationId}`);
        }
    }
    async cancelAll() {
        this.scheduledNotifications.forEach((timeoutId) => {
            clearTimeout(timeoutId);
        });
        this.scheduledNotifications.clear();
        this.pendingNotifications.clear();
        this.clearStorage();
        console.log('[WebNotificationWrapper] All notifications cancelled');
    }
    async cancelByTag(tag) {
        const toCancel = [];
        this.pendingNotifications.forEach((notification, notificationId) => {
            if (notification.tag === tag) {
                toCancel.push(notificationId);
            }
        });
        for (const notificationId of toCancel) {
            await this.cancel(notificationId);
        }
        console.log(`[WebNotificationWrapper] Cancelled ${toCancel.length} notifications with tag: ${tag}`);
    }
    async getPending() {
        return Array.from(this.pendingNotifications.values());
    }
    // Private methods for localStorage persistence
    savePendingNotifications() {
        try {
            const notifications = Array.from(this.pendingNotifications.entries());
            const serialized = notifications.map(([id, notification]) => ({
                id,
                ...notification,
                scheduleTime: notification.scheduleTime.toISOString()
            }));
            localStorage.setItem(STORAGE_KEY, JSON.stringify(serialized));
        }
        catch (error) {
            console.error('[WebNotificationWrapper] Failed to save pending notifications:', error);
        }
    }
    saveScheduledNotifications() {
        try {
            const scheduled = Array.from(this.scheduledNotifications.entries());
            localStorage.setItem(STORAGE_KEY_SCHEDULED, JSON.stringify(scheduled));
        }
        catch (error) {
            console.error('[WebNotificationWrapper] Failed to save scheduled notifications:', error);
        }
    }
    async restorePendingNotifications() {
        try {
            const pendingData = localStorage.getItem(STORAGE_KEY);
            // const scheduledData = localStorage.getItem(STORAGE_KEY_SCHEDULED)
            if (pendingData) {
                const notifications = JSON.parse(pendingData);
                const now = new Date();
                for (const notificationData of notifications) {
                    const scheduleTime = new Date(notificationData.scheduleTime);
                    const delay = scheduleTime.getTime() - now.getTime();
                    // Only restore notifications that are still in the future
                    if (delay > 0) {
                        const notification = {
                            id: notificationData.id,
                            title: notificationData.title,
                            body: notificationData.body,
                            icon: notificationData.icon,
                            badge: notificationData.badge,
                            tag: notificationData.tag,
                            data: notificationData.data,
                            requireInteraction: notificationData.requireInteraction,
                            silent: notificationData.silent,
                            scheduleTime
                        };
                        this.pendingNotifications.set(notificationData.id, notification);
                        // Re-schedule the timeout
                        const timeoutId = window.setTimeout(async () => {
                            try {
                                await this.sendImmediate(notification);
                                this.pendingNotifications.delete(notificationData.id);
                                this.scheduledNotifications.delete(notificationData.id);
                                this.savePendingNotifications();
                                console.log(`[WebNotificationWrapper] Restored notification sent: ${notification.title}`);
                            }
                            catch (notificationError) {
                                console.error('[WebNotificationWrapper] Failed to send restored notification:', notificationError);
                                this.pendingNotifications.delete(notificationData.id);
                                this.scheduledNotifications.delete(notificationData.id);
                                this.savePendingNotifications();
                            }
                        }, delay);
                        this.scheduledNotifications.set(notificationData.id, timeoutId);
                    }
                    else {
                        // Remove expired notifications
                        console.log(`[WebNotificationWrapper] Skipping expired notification: ${notificationData.title}`);
                    }
                }
                this.savePendingNotifications();
                this.saveScheduledNotifications();
                console.log(`[WebNotificationWrapper] Restored ${this.pendingNotifications.size} pending notifications`);
            }
        }
        catch (error) {
            console.error('[WebNotificationWrapper] Failed to restore pending notifications:', error);
            // Clear corrupted data
            this.clearStorage();
        }
    }
    clearStorage() {
        try {
            localStorage.removeItem(STORAGE_KEY);
            localStorage.removeItem(STORAGE_KEY_SCHEDULED);
        }
        catch (error) {
            console.error('[WebNotificationWrapper] Failed to clear storage:', error);
        }
    }
    setupEventListeners() {
        // Listen for page visibility changes to optimize notifications
        document.addEventListener('visibilitychange', () => {
            if (document.hidden) {
                console.log('[WebNotificationWrapper] Page hidden - notifications will be sent as scheduled');
            }
            else {
                console.log('[WebNotificationWrapper] Page visible - considering in-app notifications');
            }
        });
        // Listen for beforeunload to clean up
        window.addEventListener('beforeunload', () => {
            this.savePendingNotifications();
            this.saveScheduledNotifications();
        });
    }
    isMobileBrowser() {
        // Check for mobile browsers that require service worker notifications
        const userAgent = navigator.userAgent || navigator.vendor || window.opera;
        // Mobile Safari, Chrome Mobile, Firefox Mobile, Samsung Browser, etc.
        const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS|FxiOS|SamsungBrowser/i;
        return mobileRegex.test(userAgent);
    }
    async sendServiceWorkerNotification(payload) {
        try {
            // Get the service worker registration
            let registration = await navigator.serviceWorker.getRegistration();
            // If no registration exists, register one
            if (!registration && this.config.serviceWorkerPath) {
                registration = await navigator.serviceWorker.register(this.config.serviceWorkerPath);
            }
            if (!registration) {
                throw new Error('Service worker not available for mobile notifications');
            }
            // Use service worker registration to show notification
            const notificationOptions = {
                body: payload.body,
                icon: payload.icon || '/logo-192x192.avif',
                badge: payload.badge || '/logo-192x192.avif',
                tag: payload.tag,
                data: payload.data,
                requireInteraction: payload.requireInteraction,
                silent: payload.silent
            };
            // Add actions if supported and available
            if (payload.data?.actions && 'actions' in notificationOptions) {
                notificationOptions.actions = payload.data.actions;
            }
            await registration.showNotification(payload.title, notificationOptions);
            console.log('[WebNotificationWrapper] Service worker notification sent:', payload.title);
        }
        catch (error) {
            console.error('[WebNotificationWrapper] Service worker notification failed:', error);
            throw error;
        }
    }
    handleNotificationClick(payload) {
        // Focus the window
        if (window.focus) {
            window.focus();
        }
        // Dispatch custom event
        const event = {
            type: 'notification-click',
            payload,
            platform: this.platform
        };
        window.dispatchEvent(new CustomEvent('notification-click', { detail: event }));
        // If there's a URL in the data, navigate to it
        if (payload.data?.url) {
            window.location.href = payload.data.url;
        }
        console.log('[WebNotificationWrapper] Notification clicked:', payload.title);
    }
}
